import { useState } from 'react'

// Single error in the errors array
interface MemberError {
  member_id: string
  member_name: string
  plan_year?: number | null
  operation_name: string
  error_message: string
  error_class: string
  backtrace?: string[]
}

// Single error format (backward compatibility)
interface SingleErrorDetails {
  class?: string
  operation_name?: string
  backtrace?: string[]
}

// Multiple errors format (new)
interface MultipleErrorsDetails {
  message: string
  error_count: number
  errors: MemberError[]
}

type ErrorDetails = SingleErrorDetails | MultipleErrorsDetails

interface ErrorOperation {
  id: string
  timestamp: string
  errorMessage: string
  errorMemberId?: string
  errorMemberName?: string
  errorMemberPath?: string
  goodJobId?: string
  betterstackLogsUrl?: string
  errorDetails?: ErrorDetails
}

interface Props {
  operations: ErrorOperation[]
  adminGoodJobPath: string
  adminMemberPathTemplate?: string // e.g., "/admin/members/:id"
  operationType: 'generation' | 'finalization' | 'charge_status_update'
}

function isMultipleErrorsDetails (details: ErrorDetails): details is MultipleErrorsDetails {
  return 'errors' in details && Array.isArray(details.errors)
}

function StackTraceDetails ({
  errorClass,
  operationName,
  backtrace,
  size = 'sm',
}: {
  errorClass?: string
  operationName?: string
  backtrace?: string[]
  size?: 'xs' | 'sm'
}) {
  const [isOpen, setIsOpen] = useState(false)

  if (!errorClass && !operationName && (!backtrace || backtrace.length === 0)) {
    return null
  }

  const textSize = size === 'xs' ? 'text-xs' : 'text-sm'
  const summarySize = size === 'xs' ? 'text-xs' : 'text-sm'

  return (
    <details
      className='mt-2'
      open={isOpen}
      onToggle={(e) => {
        e.stopPropagation()
        setIsOpen((e.target as HTMLDetailsElement).open)
      }}
    >
      <summary className={`cursor-pointer ${summarySize} font-semibold text-red-700 hover:text-red-600`}>
        {size === 'xs' ? 'View trace' : 'View stack trace'}
      </summary>
      <div className={`mt-2 space-y-2 ${textSize} bg-white p-2 rounded border border-red-200`}>
        {errorClass && (
          <div>
            <span className='font-semibold'>Error class:</span>
            <span className='font-mono ml-1'>{errorClass}</span>
          </div>
        )}
        {operationName && (
          <div>
            <span className='font-semibold'>Operation:</span>
            <span className='ml-1'>{operationName}</span>
          </div>
        )}
        {backtrace && backtrace.length > 0 && (
          <div>
            <span className='font-semibold block mb-1'>
              Stack trace{size === 'sm' ? ' (first 10 lines)' : ''}:
            </span>
            <pre className={`p-2 ${size === 'xs' ? 'bg-gray-50' : 'bg-white border border-red-200'} rounded text-xs overflow-x-auto`}>
              {backtrace.join('\n')}
            </pre>
          </div>
        )}
      </div>
    </details>
  )
}

function GlobalErrorDisplay ({
  errorMessage,
  errorDetails,
  operationType,
}: {
  errorMessage: string
  errorDetails?: SingleErrorDetails
  operationType: 'generation' | 'finalization' | 'charge_status_update'
}) {
  const operationLabels: Record<typeof operationType, string> = {
    generation: 'Generation',
    finalization: 'Finalization',
    charge_status_update: 'Payment',
  }
  const operationLabel = operationLabels[operationType]

  return (
    <div className='space-y-3'>
      {/* Error Message */}
      <div>
        <div className='font-semibold text-red-900 mb-1'>{operationLabel} Error Message:</div>
        <div className='font-mono text-sm text-red-800 bg-white p-2 rounded border border-red-200'>
          {errorMessage}
        </div>
      </div>

      {/* Technical Details */}
      {errorDetails && (
        <StackTraceDetails
          errorClass={errorDetails.class}
          operationName={errorDetails.operation_name}
          backtrace={errorDetails.backtrace}
          size='sm'
        />
      )}
    </div>
  )
}

function MemberErrorTableRow ({
  error,
  adminMemberPathTemplate,
}: {
  error: MemberError
  adminMemberPathTemplate?: string
}) {
  const memberPath = adminMemberPathTemplate?.replace(':id', error.member_id)
  const isTruncationSummary = error.member_id === 'truncated'

  // Special styling for truncation summary row
  if (isTruncationSummary) {
    return (
      <tr className='border-b border-gray-200 last:border-b-0 bg-yellow-50'>
        <td colSpan={3} className='py-3 px-4'>
          <div className='flex items-start gap-2'>
            <span className='text-yellow-600 font-semibold'>⚠️</span>
            <div>
              <div className='font-semibold text-yellow-900'>{error.member_name}</div>
              <div className='text-sm text-yellow-800 mt-1'>{error.error_message}</div>
            </div>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr className='border-b border-gray-200 last:border-b-0 hover:bg-gray-50'>
      <td className='py-3 px-4 align-top'>
        {memberPath
          ? (
            <a
              href={memberPath}
              className='text-blue-600 hover:underline font-medium'
            >
              {error.member_name}
            </a>
            )
          : (
            <span className='font-medium'>{error.member_name}</span>
            )}
        <div className='text-xs text-gray-500 mt-1'>{error.member_id}</div>
      </td>
      <td className='py-3 px-4 align-top'>
        <div className='text-sm'>{error.operation_name}</div>
        {error.plan_year !== null && error.plan_year !== undefined && (
          <div className='text-xs text-gray-600 mt-1'>Year: {error.plan_year}</div>
        )}
      </td>
      <td className='py-3 px-4 align-top'>
        <div className='text-sm text-red-800'>{error.error_message}</div>
        <StackTraceDetails
          errorClass={error.error_class}
          backtrace={error.backtrace}
          size='xs'
        />
      </td>
    </tr>
  )
}

function MemberErrorTable ({
  errors,
  adminMemberPathTemplate,
}: {
  errors: MemberError[]
  adminMemberPathTemplate?: string
}) {
  const showScrollContainer = errors.length > 4

  return (
    <div className='bg-white rounded border border-red-200 overflow-hidden'>
      <div className={showScrollContainer ? 'max-h-96 overflow-y-auto' : ''}>
        <table className='w-full'>
          <thead className='bg-gray-50 border-b border-gray-200 sticky top-0'>
            <tr>
              <th className='py-2 px-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                Member
              </th>
              <th className='py-2 px-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                Operation
              </th>
              <th className='py-2 px-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                Error
              </th>
            </tr>
          </thead>
          <tbody>
            {errors.map((error, index) => (
              <MemberErrorTableRow
                key={`${error.member_id}-${index}`}
                error={error}
                adminMemberPathTemplate={adminMemberPathTemplate}
              />
            ))}
          </tbody>
        </table>
      </div>
      {showScrollContainer && (
        <div className='text-xs text-gray-500 text-center py-2 bg-gray-50 border-t border-gray-200'>
          Scroll to view all {errors.length} errors
        </div>
      )}
    </div>
  )
}

function LogLinks ({
  goodJobId,
  betterstackLogsUrl,
  adminGoodJobPath,
}: {
  goodJobId?: string
  betterstackLogsUrl?: string
  adminGoodJobPath: string
}) {
  if (!goodJobId) return null

  return (
    <div className='flex gap-3'>
      <a
        href={`${adminGoodJobPath}/jobs/${goodJobId}`}
        className='button-outline text-sm'
        target='_blank'
        rel='noopener noreferrer'
      >
        View job in GoodJob
      </a>
      {betterstackLogsUrl && (
        <a
          href={betterstackLogsUrl}
          className='button-outline text-sm'
          target='_blank'
          rel='noopener noreferrer'
        >
          View logs in BetterStack
        </a>
      )}
    </div>
  )
}

export default function EmployerInvoiceOperationError ({
  operations,
  adminGoodJobPath,
  adminMemberPathTemplate = '/admin/members/:id',
  operationType,
}: Props) {
  const currentOperation = operations[0]
  if (!currentOperation) return null

  const {
    errorMessage,
    errorMemberId,
    errorMemberPath: _errorMemberPath,
    errorMemberName,
    goodJobId,
    betterstackLogsUrl,
    errorDetails,
  } = currentOperation

  const hasMultipleErrors = errorDetails && isMultipleErrorsDetails(errorDetails)

  // Check if this is a member-specific error or a global system error
  const isMemberError = hasMultipleErrors || errorMemberId

  if (!isMemberError) {
    // Global system error - use simple display
    return (
      <div className='space-y-3 bg-red-50 p-4 rounded-lg border border-red-200 transition-all'>
        <GlobalErrorDisplay
          errorMessage={errorMessage}
          errorDetails={errorDetails as SingleErrorDetails}
          operationType={operationType}
        />
        <LogLinks
          goodJobId={goodJobId}
          betterstackLogsUrl={betterstackLogsUrl}
          adminGoodJobPath={adminGoodJobPath}
        />
      </div>
    )
  }

  // Member-specific error(s) - use table format
  const errors: MemberError[] = hasMultipleErrors
    ? errorDetails.errors
    : [
        {
          member_id: errorMemberId || 'unknown',
          member_name: errorMemberName || 'Unknown Member',
          plan_year: null,
          operation_name: errorDetails?.operation_name || 'Unknown',
          error_message: errorMessage,
          error_class: errorDetails?.class || 'Error',
          backtrace: errorDetails?.backtrace,
        },
      ]

  const summaryMessage = hasMultipleErrors
    ? errorDetails.message
    : 'Operation error'

  return (
    <div className='space-y-3 bg-red-50 p-4 rounded-lg border border-red-200 transition-all'>
      {/* Summary */}
      <div>
        <div className='font-semibold text-red-900 mb-1'>
          ⚠️ {summaryMessage}
        </div>
      </div>

      {/* Error Table */}
      <MemberErrorTable
        errors={errors}
        adminMemberPathTemplate={adminMemberPathTemplate}
      />

      {/* Shared Links Section */}
      <LogLinks
        goodJobId={goodJobId}
        betterstackLogsUrl={betterstackLogsUrl}
        adminGoodJobPath={adminGoodJobPath}
      />
    </div>
  )
}
